import type { ChipDto } from '@pietrochip/shared';
import { useId, useState } from 'react';
import { personName, time } from '../lib/format';

/**
 * I chip archiviati, con la strada per rimetterli.
 *
 * Serve a una cosa sola: rendere reversibile la rimozione. Al banco un codice
 * sbagliato si toglie in un secondo, e chi lo toglie non deve temere di aver
 * perso niente — il chip è qui, con il codice, l'ora e chi l'ha rimosso, e
 * «Ripristina» lo rimette esattamente com'era, restituzione compresa.
 *
 * La sezione resta chiusa finché non la si apre, e l'elenco si chiede al server
 * solo la prima volta che si apre: un evento senza rimossi — la stragrande
 * maggioranza — non paga niente, e chi apre trova già i dati. Dopo il primo
 * caricamento sono i messaggi del socket a tenerlo aggiornato.
 */
export function RemovedChips({
  chips,
  loading,
  busyChipId,
  onOpen,
  onRestore,
}: {
  chips: readonly ChipDto[];
  loading: boolean;
  busyChipId: string | null;
  /** Chiamata la prima volta che la sezione viene aperta. */
  onOpen: () => void;
  onRestore: (chip: ChipDto) => void;
}) {
  const [open, setOpen] = useState(false);
  const listId = useId();

  // Il conteggio compare solo dopo il primo caricamento: prima sarebbe uno zero
  // che non sappiamo ancora se è vero.
  const count = loading && chips.length === 0 ? null : chips.length;

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) onOpen();
  }

  return (
    <section className="removed">
      <button
        type="button"
        className="removed__toggle"
        aria-expanded={open}
        aria-controls={listId}
        onClick={toggle}
      >
        <span className="removed__caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span>
          Chip rimossi
          {count !== null && count > 0 && <span className="removed__count"> {count}</span>}
        </span>
        <span className="shell__spacer" />
        <span className="removed__hint">
          {open ? 'Chiudi' : 'Archiviati, si possono rimettere'}
        </span>
      </button>

      {open && (
        <div className="removed__body" id={listId}>
          {loading && chips.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.875rem' }}>
              Caricamento…
            </p>
          ) : chips.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.875rem' }}>
              Nessun chip rimosso. Quello che si toglie dall’elenco finisce qui, e da qui si
              ripristina.
            </p>
          ) : (
            <ul className="removed__list">
              {chips.map((chip) => (
                <li key={chip.id} className="removed__item">
                  <div style={{ minWidth: 0 }}>
                    <div className="mono removed__code">{chip.code}</div>
                    <div className="removed__meta">
                      {chip.lastName ? `${personName(chip.firstName, chip.lastName)} · ` : ''}
                      {chip.team ? `${chip.team} · ` : ''}
                      rimosso{chip.removedAt ? ` alle ${time(chip.removedAt)}` : ''}
                      {chip.removedByName ? ` da ${chip.removedByName}` : ''}
                    </div>
                    {chip.returnedAt !== null && (
                      <div className="removed__meta">
                        Restituzione registrata
                        {chip.returnedAt ? ` alle ${time(chip.returnedAt)}` : ''}
                        {chip.returnedByName ? ` da ${chip.returnedByName}` : ''} — resta nel
                        registro.
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn btn--sm"
                    disabled={busyChipId === chip.id}
                    onClick={() => onRestore(chip)}
                    aria-label={`Ripristina il chip ${chip.code} nell’elenco`}
                  >
                    {busyChipId === chip.id ? '…' : 'Ripristina'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
